import { RecentInvoiceAndReturnsEntry } from "../hooks/useRecentInvoiceAndReturns";
import { formatDate } from "./calculations";
import type { Cell } from "write-excel-file/browser";

export type InvoiceAndReturnsExportParams = {
	ledgerEntries: RecentInvoiceAndReturnsEntry[];
	startDate: string;
	endDate: string;
};

export type InvoiceAndReturnsExportRow = {
	invoiceNumber: string;
	date: string;
	type: RecentInvoiceAndReturnsEntry["type"];
	customer: string;
	amount: number;
	cash: number;
	upi: number;
	outstanding: number;
	profit: number;
};

export type InvoiceAndReturnsExportTotals = {
	amount: number;
	cash: number;
	upi: number;
	outstanding: number;
	profit: number;
};

export const invoiceAndReturnsExportHeaders = [
	"Invoice Number",
	"Date",
	"Type",
	"Customer",
	"Sale Amount (INR)",
	"CASH (INR)",
	"UPI (INR)",
	"Outstanding (INR)",
	"Profit (INR)"
];

export const invoiceAndReturnsTypeColors = {
	Invoice: {
		hex: "#008000",
		rgb: [0, 128, 0] as [number, number, number]
	},
	Return: {
		hex: "#FF0000",
		rgb: [255, 0, 0] as [number, number, number]
	},
	Payment: {
		hex: "#0000FF",
		rgb: [0, 0, 255] as [number, number, number]
	}
};

export const currencyFormat = "#,##0.00";

export const excelHeaderCell = (value: string): Cell => ({
	value,
	fontWeight: "bold",
	backgroundColor: "#F2F2F2"
});

export const excelNumberCell = (value: number, fontWeight?: "bold"): Cell => ({
	value,
	type: Number,
	format: currencyFormat,
	fontWeight
});

export const excelTypeCell = (
	value: keyof typeof invoiceAndReturnsTypeColors
): Cell => ({
	value,
	textColor: invoiceAndReturnsTypeColors[value].hex
});

export const pdfTypeCell = (
	value: keyof typeof invoiceAndReturnsTypeColors
) => ({
	content: value,
	styles: {
		textColor: invoiceAndReturnsTypeColors[value].rgb
	}
});

export const formatInvoiceAndReturnsAmount = (value: number) =>
	value.toFixed(2);

export const buildInvoiceAndReturnsExportFileName = (
	startDate: string,
	endDate: string,
	extension: "xlsx" | "pdf"
) => `invoice-returns-report_${startDate}_to_${endDate}.${extension}`;

export const prepareInvoiceAndReturnsExportData = (
	ledgerEntries: RecentInvoiceAndReturnsEntry[]
) => {
	const totals: InvoiceAndReturnsExportTotals = {
		amount: 0,
		cash: 0,
		upi: 0,
		outstanding: 0,
		profit: 0
	};

	const rows: InvoiceAndReturnsExportRow[] = ledgerEntries.map((entry) => {
		if (entry.type === "Invoice" || entry.type === "Return") {
			totals.amount += entry.amount;
		}
		totals.cash += entry.cash;
		totals.upi += entry.upi;
		totals.outstanding += entry.outstanding;
		totals.profit += entry.profit;

		return {
			invoiceNumber: entry.invoiceNumber,
			date: formatDate(entry.date),
			type: entry.type,
			customer: entry.details,
			amount: entry.amount,
			cash: entry.cash,
			upi: entry.upi,
			outstanding: entry.outstanding,
			profit: entry.profit
		};
	});

	return { rows, totals };
};
